import parseISO from "date-fns/parseISO";
import { ThunkDispatch } from "redux-thunk";

import {
    getAuthenticationContext,
    getBaseStore,
} from "@utils/configuration/aem";
import {
    PageTemplates,
    isPageTemplate,
} from "@utils/configuration/pageTemplate.helpers";
import { logoutCleanUp } from "@utils/customer";
import { HeaderKeys } from "@utils/enums";

import { AlgoliaTrackingService } from "@services/algolia/tracking/algoliaTracking.service";
import CallService, { CallServiceKey } from "@services/call.service";
import * as WishlistService from "@services/localWishlist.service";
import { PreferredStoreService } from "@services/preferredStore.service";
import { SoftLoginService } from "@services/softLogin.service";
import { trackLeadImpression } from "@services/tracking/trackers/leadImpressionTracker";
import { trackRegistrationStepSubmitted } from "@services/tracking/trackers/tools/registration/toolRegistrationStepTracker";

import createApiModule from "@redux/create-api-module";
import { getTranslations } from "@redux/modules/value/translations/translations.selector";
import { IState, TApiAction, TExtraThunkArgs } from "@redux/store.types";
import { getApiDataState } from "@redux/utils";

import { MAX_WISHLIST_ITEMS } from "@components/Wishlist/wishlist.config";

import { WishlistModel } from "../wishlist/@types/wishlist.model";
import {
    APIStatus as WishlistApiStatus,
    addItemsToList,
    createWishlist,
    loadAll,
} from "../wishlist/wishlist.module";
import { CustomerModel } from "./@types/customer.model";
import { CustomerMapper, UpdateCustomerMapper } from "./customer.mapper";
import { $CustomerModuleName, SubscriptionRequest } from "./customer.types";

const { actions, constants } = createApiModule(
    $CustomerModuleName,
    data => `/customerservice/${getBaseStore()}/customers${data?.url || ""}`,
    {
        mapper: CustomerMapper,
        transformResponse: (customer: CustomerModel) => {
            if (
                customer &&
                Object.prototype.hasOwnProperty.call(customer, "email")
            ) {
                if (!customer.guest) {
                    const storeId = customer?.favouriteStoreId;
                    if (storeId) {
                        PreferredStoreService.setPreferredStoreOfTheUser(
                            storeId,
                        );
                    } else {
                        PreferredStoreService.removePreferredStoreOfTheUser();
                    }
                }

                SoftLoginService.clearSoftLogin();
                if (!customer.guest) {
                    SoftLoginService.setSoftLogin(
                        customer.email,
                        customer.firstName,
                    );

                    AlgoliaTrackingService.authenticate();
                }
            }

            return customer;
        },
    },
);

const handleWishlistMerge = async (
    dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>,
    getState: () => IState,
) => {
    const localWishlist = WishlistService.getLocalWishlist();
    if (localWishlist.length === 0) {
        return;
    }

    const loadResult = await dispatch(loadAll(true));
    if (loadResult.type === WishlistApiStatus.SUCCESS) {
        const translations = getTranslations(getState());

        const newListItems = [...(localWishlist || [])];

        let userList: Partial<WishlistModel> = {
            products: [],
        };

        if (
            newListItems.length > 0 &&
            Array.isArray(loadResult.payload) &&
            loadResult.payload.length > 0
        ) {
            if (loadResult.payload.length === 1) {
                userList = loadResult.payload[0];
            }
            if (loadResult.payload.length > 1) {
                const localName = WishlistService.getNewName([], translations);
                userList =
                    loadResult.payload.find(
                        l => l.name.toLowerCase() === localName.toLowerCase(),
                    ) ||
                    loadResult.payload
                        .sort((a, b) => {
                            const aDate = parseISO(a.validTo);
                            const bDate = parseISO(b.validTo);
                            return aDate.getTime() - bDate.getTime();
                        })
                        .at(0);
            }
        }

        const newLength =
            newListItems.length + (userList?.products || []).length;
        if (newLength <= MAX_WISHLIST_ITEMS && userList.id) {
            const createResult = await dispatch(
                addItemsToList(
                    userList.id,
                    newListItems.map(i => i.sku),
                    [], // don't track merged items, they already have been tracked.
                ),
            );

            if (createResult.type === WishlistApiStatus.SUCCESS) {
                WishlistService.removeLocalWishlist();
            }
        } else {
            const wishlistName = WishlistService.getNewName(
                loadResult.payload,
                translations,
            );

            const createResult = await dispatch(
                createWishlist(wishlistName, newListItems),
            );

            if (createResult.type === WishlistApiStatus.SUCCESS) {
                WishlistService.removeLocalWishlist();
            }
        }
    }
};

export const logout =
    () =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/logout`,
                    },
                },
                "GET",
            ),
        );

        if (action.type === constants.SUCCESS) {
            logoutCleanUp();
            CallService.enable(CallServiceKey.Customer);
        }

        return Promise.resolve(action);
    };

export const editEmail =
    (email: string, password: string) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/changeemail`,
                    },
                    data: { email, password },
                },
                "PUT",
            ),
        );

export const login =
    (email: string, password: string) =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>,
        getState: () => IState,
    ) => {
        const loadResult = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/login`,
                    },
                    data: { email, password },
                },
                "POST",
            ),
        );

        if (loadResult.type === constants.SUCCESS) {
            CallService.enable(CallServiceKey.Cart);
            CallService.enable(CallServiceKey.Customer);
            await handleWishlistMerge(dispatch, getState);
        }

        return loadResult;
    };

export const register =
    (
        email: string,
        firstName: string,
        language: string,
        lastName: string,
        password: string,
    ) =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>,
        getState: () => IState,
    ) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/register`,
                    },
                    data: { email, firstName, language, lastName, password },
                },
                "POST",
            ),
        );

        if (action.type === constants.SUCCESS) {
            trackRegistrationStepSubmitted(action.payload);
            CallService.enable(CallServiceKey.Cart);
            await handleWishlistMerge(dispatch, getState);
        }

        return Promise.resolve(action);
    };

export const registerGuest =
    (email: string, language: string) =>
    (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        return dispatch(
            actions.load(
                {
                    path: {
                        url: `/registerguest`,
                    },
                    data: { email, language },
                },
                "POST",
            ),
        );
    };

export const confirmGuest =
    (orderId: string, password: string) =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>,
        getState: () => IState,
    ) => {
        const loadResult = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/confirmguest`,
                    },
                    data: { password },
                    headers: {
                        [HeaderKeys.orderIdPublic]: orderId,
                    },
                },
                "POST",
            ),
        );

        if (loadResult.type === constants.SUCCESS) {
            CallService.enable(CallServiceKey.Cart);
            await handleWishlistMerge(dispatch, getState);
        }

        return loadResult;
    };

export const forgotPassword =
    (email: string) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/requestresetpassword`,
                    },
                    data: { email },
                },
                "POST",
            ),
        );

export const resetPassword =
    (password: string, token: string, email: string) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/resetpassword`,
                    },
                    data: { email, token, password },
                },
                "POST",
            ),
        );

export const changePassword =
    (oldPassword: string, newPassword: string) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/changepassword`,
                    },
                    data: { oldPassword, newPassword },
                },
                "PUT",
            ),
        );

export const updateFromData =
    (data: CustomerModel) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: "",
                    },
                    data: UpdateCustomerMapper(data),
                },
                "PUT",
                true,
                { mergeParams: false },
            ),
        );

export const load =
    () =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>,
        getState: () => IState,
    ) => {
        if (!CallService.shouldCall(CallServiceKey.Customer, { minutes: 14 })) {
            return {
                type: constants.SUCCESS,
                payload: getApiDataState(getState(), $CustomerModuleName)?.data,
                actionOptions: {
                    skipErrors: [401, 404],
                },
            } as TApiAction;
        }

        const result = await dispatch(
            actions.load(
                {
                    path: {
                        url: "",
                    },
                },
                "GET",
                false,
                {
                    skipErrors: [401, 404],
                },
            ),
        );

        if (result.type === constants.SUCCESS) {
            dispatch(loadAll(true));
            CallService.enable(CallServiceKey.Customer);
            const hrefPath = new URL(location.href).pathname;
            const loginPath = new URL(getAuthenticationContext().loginPageUrl)
                .pathname;

            if (
                result.payload.guest &&
                isPageTemplate(PageTemplates.ACCOUNT) &&
                hrefPath !== loginPath &&
                getAuthenticationContext().authenticationRequired
            ) {
                location.assign(getAuthenticationContext().loginPageUrl);
            }
        }

        if (result.type === constants.FAILURE) {
            CallService.disable(CallServiceKey.Customer);
        }

        return result;
    };

export const subscribeToNewsLetter =
    (data: SubscriptionRequest) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/newsletter/subscribe`,
                    },
                    data,
                },
                "POST",
                false,
            ),
        );

        if (action.type === constants.SUCCESS) {
            trackLeadImpression();
        }

        return Promise.resolve(action);
    };

export const unsubscribeToNewsLetter =
    (email: string) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/newsletter/unsubscribe?email=${email}`,
                    },
                },
                "PUT",
                false,
                {},
            ),
        );

export const clear = actions.clear;
export const refetch = actions.refetch;
export const APIStatus = constants;
